import React, { useState, useEffect, useMemo } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";
import {
  getPublicEvent,
  getPublicEventGifts,
  getEventAccessToken,
  type PublicEvent,
} from "@/services/api/publicEvents";
import type { LandingDraftOverrides, LandingDraftPayload } from "@/services/api/eventSettings";
import { decodeDraftParam, shouldApplyDraft, DRAFT_PARAM, PREVIEW_PARAM } from "@/utils/previewLink";
import { Card, CardContent } from "@/components/ui/card";
import NotFound from "@/pages/NotFound";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { renderEventTemplate } from "@/pages/public/templates";
import { getFallbackTemplate, EVENT_TEMPLATE_DEFAULT, type EventTemplateId } from "@/lib/event-templates";
import { TemplatePageHost } from "@/lib/template-engine/TemplatePageHost";
import { paletteConfigFor, resolveTemplate } from "@/lib/template-engine/resolve";
import { PUBLIC_ROUTES } from "../../routes";
import { PasswordGate } from "@/components/public/PasswordGate";
import { PreviewDraftBanner } from "@/components/public/PreviewDraftBanner";
import { usePageScopedLanguage } from "@/hooks/usePageScopedLanguage";
import { usePageMeta } from "@/hooks/usePageMeta";
import { publicEventPageTitle } from "@/lib/public-event-meta";

export default function EventPublicPage() {
  const { t, currentLanguage } = useTranslation();
  // JUB-81: the event language is applied for this page only — never persisted
  // as the visitor's preference, and restored when the page unmounts.
  const setPageLanguage = usePageScopedLanguage();
  const { slug, locale } = useParams<{ slug: string; locale?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [event, setEvent] = useState<PublicEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draftPayload, setDraftPayload] = useState<LandingDraftPayload | null>(null);
  // JUB-184: the public event API answered 404 for this slug.
  const [notFound, setNotFound] = useState(false);
  const [hasEnabledGifts, setHasEnabledGifts] = useState<boolean>(false);
  const [effectiveLocale, setEffectiveLocale] = useState<string>(locale || (navigator.language?.startsWith('en') ? 'en' : 'ro'));
  const [passwordValidated, setPasswordValidated] = useState<boolean>(false);
  const [passwordRequired, setPasswordRequired] = useState<boolean>(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  // JUB-73/JUB-64: counts consecutive old-slug redirects so a bad
  // previous_url chain can never loop forever. Reset on a successful load.
  const slugRedirectHopsRef = React.useRef(0);
  
  // Handle @ routes - extract slug from pathname if route starts with /@
  const actualSlug = useMemo(() => {
    if (location.pathname.startsWith('/@')) {
      // Extract slug from /@{slug} pattern
      const match = location.pathname.match(/^\/@(.+)$/);
      const extractedSlug = match ? match[1] : null;
      return extractedSlug;
    }
    return slug || null;
  }, [location.pathname, slug]);

  useEffect(() => {
    if (actualSlug) {
      setNotFound(false);
      loadEvent();
    } else {
      console.warn('[EventPublicPage] No slug available, cannot load event');
    }
  }, [actualSlug, locale]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const rawDraft = params.get(DRAFT_PARAM);
    if (!rawDraft) {
      setDraftPayload(null);
      return;
    }
    setDraftPayload(decodeDraftParam(rawDraft));
  }, [location.search]);

  const loadEvent = async (tokenOverride?: string) => {
    if (!actualSlug) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Use provided token, state token, or try to get stored token from localStorage
      const tokenToUse = tokenOverride || accessToken || getEventAccessToken(actualSlug);
      if (tokenToUse && !accessToken) {
        // Update state if we found a stored token
        setAccessToken(tokenToUse);
      }
      // The owner's server-signed preview token (JUB-151) lifts the live gate
      // and the password gate for their own preview (JUB-125). Read it straight
      // from the URL: state may not be set yet on the first load.
      const previewToken = new URLSearchParams(location.search).get(PREVIEW_PARAM);
      const eventData = await getPublicEvent(actualSlug, tokenToUse, previewToken);

      // JUB-73/JUB-64: the slug changed (event was renamed) — the backend
      // answers with { redirect_to: newSlug } instead of an HTTP 302 (which
      // the XHR followed into an SPA route and rendered a blank page).
      // Follow it with a client-side navigation to the new slug.
      if (eventData.redirect_to) {
        slugRedirectHopsRef.current += 1;
        if (slugRedirectHopsRef.current > 2) {
          setError(t('common.failedToLoadEvent'));
          return;
        }
        const newSlug = eventData.redirect_to;
        const target = location.pathname.startsWith('/@')
          ? `/@${newSlug}`
          : PUBLIC_ROUTES.publicEvent(effectiveLocale === 'en' ? 'en' : 'ro', newSlug);
        navigate(target, { replace: true });
        return;
      }
      slugRedirectHopsRef.current = 0;

      setEvent(eventData);
      
      // If we successfully loaded a password-protected event, mark it as validated
      // (we either had a valid token or password protection is disabled)
      if (eventData?.passwordEnabled && tokenToUse) {
        setPasswordValidated(true);
      }
      
      // The public page renders in the event's default language (PO decision on
      // JUB-81: no locale in the URL, no guest switcher). Fallback: URL locale,
      // then the browser language, then 'ro'.
      // EventResource always emits `defaultLanguage` (defaulting to 'ro') for the
      // public payload, but the shared PublicEvent type does not declare it yet, and
      // the `redirect_to` short-circuit response above carries no event fields at all
      // — so read it defensively and keep the existing fallback chain (JUB-132).
      const eventDefaultLanguage =
        "defaultLanguage" in eventData && typeof eventData.defaultLanguage === "string"
          ? eventData.defaultLanguage
          : undefined;
      const determinedLocale = eventDefaultLanguage
        || locale
        || (navigator.language?.startsWith('en') ? 'en' : 'ro');

      setEffectiveLocale(determinedLocale);

      // Apply the event language to this page only: the visitor's app-wide
      // preference (i18nextLng) stays untouched and the previous language is
      // restored on unmount (usePageScopedLanguage).
      if (eventDefaultLanguage && eventDefaultLanguage !== currentLanguage) {
        void setPageLanguage(eventDefaultLanguage);
      }
      try {
        // Determine if there are any enabled gifts (public gifts API only returns enabled items)
        const giftsResp = await getPublicEventGifts(actualSlug, tokenToUse, previewToken);
        const gifts = Array.isArray(giftsResp?.gifts) ? giftsResp.gifts : [];
        setHasEnabledGifts(gifts.length > 0);
      } catch {
        setHasEnabledGifts(false);
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { error?: { code?: string; message?: string; message_key?: string } } }; message?: string };
      console.error('Failed to load event:', err);

      // JUB-151: an expired or foreign preview link — show the page as any
      // visitor would see it rather than an error.
      if (axiosErr.response?.status === 403 && axiosErr.response?.data?.error?.code === 'preview_token_invalid') {
        window.location.replace(location.pathname);
        return;
      }

      // Check if this is a password-protected event (401 with password_required code)
      if (axiosErr.response?.status === 401 && axiosErr.response?.data?.error?.code === 'password_required') {
        setPasswordRequired(true);
        setLoading(false);
        return;
      }
      
      // JUB-184: an unknown slug is a not-found page, decided after this one
      // call. Every former "alternative path" resolved to the same /@{slug} and
      // only repeated the request; the raw API message is never shown.
      if (axiosErr.response?.status === 404) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      // Not a 404: show the error
      const errorMessage = axiosErr.response?.data?.error?.message
        || axiosErr.response?.data?.error?.message_key
        || axiosErr.message
        || t('common.error');
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const displayEvent = useMemo(() => {
    if (!event) return null;
    // Only a server-confirmed owner preview may show draft overrides (JUB-151).
    if (!shouldApplyDraft(event, draftPayload)) {
      return event;
    }
    return applyDraftOverrides(event, draftPayload.overrides);
  }, [event, draftPayload]);

  const displayEventWithComputedFlags = useMemo(() => {
    if (!displayEvent) return null;
    return {
      ...displayEvent,
      hasEnabledGifts,
    } as PublicEvent & { hasEnabledGifts: boolean };
  }, [displayEvent, hasEnabledGifts]);

  // A template is either a declarative package (which only the engine can render)
  // or a legacy TSX component; the stored config's shape decides. `?template-preview=`
  // additionally renders a bundled reference package over this event, dev only.
  const resolvedTemplate = useMemo(
    () => resolveTemplate(displayEventWithComputedFlags?.template?.config, location.search),
    [displayEventWithComputedFlags?.template?.config, location.search],
  );

  // Colours for the host-owned surfaces (RSVP and gift dialogs, lightbox) of a
  // package-rendered page, taken from the package's own tokens (JUB-161).
  const packageSurfaceConfig = useMemo(
    () => resolvedTemplate.kind === "package"
      ? paletteConfigFor(resolvedTemplate.pkg, displayEventWithComputedFlags?.colorScheme)
      : undefined,
    [resolvedTemplate, displayEventWithComputedFlags?.colorScheme],
  );

  const themedEvent = useMemo(() => {
    if (!displayEventWithComputedFlags) return null;
    const templateKey = (displayEventWithComputedFlags.theme as EventTemplateId) ?? EVENT_TEMPLATE_DEFAULT;
    const templateDefinition =
      displayEventWithComputedFlags.template && displayEventWithComputedFlags.template.key === templateKey
        ? displayEventWithComputedFlags.template
        : getFallbackTemplate(templateKey);
    
    // Apply color scheme override if provided
    let config = { ...templateDefinition.config };
    if (displayEventWithComputedFlags.colorScheme) {
      // Override primary accent color while maintaining template structure
      const hex = displayEventWithComputedFlags.colorScheme;
      config = {
        ...config,
        palette: {
          ...config.palette,
          accent: hex,
          accentSoft: `${hex}33`, // Add transparency
          line: `${hex}33`,
        },
        hero: {
          ...config.hero,
          buttonBg: displayEventWithComputedFlags.colorScheme,
        },
        surfaces: {
          ...config.surfaces,
          chipBackground: `${displayEventWithComputedFlags.colorScheme}20`,
          cardBorder: `${displayEventWithComputedFlags.colorScheme}40`,
        },
      };
    }
    
    return {
      ...displayEventWithComputedFlags,
      template: {
        ...templateDefinition,
        config,
      },
      theme: templateKey,
    };
  }, [displayEventWithComputedFlags]);

  // Templates declare their interpolation values as optional; the app translator
  // declares them as required. Undefined values are dropped before the call, which
  // i18next handles exactly like a key that was never passed — same rendered text.
  const templateT = useMemo(() => toTemplateTranslator(t), [t]);

  const viewingDraft = Boolean(event?.previewMode && displayEvent);

  // JUB-181: the tab, history and share sheet name the event the way its hero
  // does. Loading, the password gate, a preview and the not-found state keep a
  // neutral title and stay out of search indexes; the guest-facing page uses
  // the event's description (or a generic line) and its cover as og:image.
  const showsEvent = Boolean(themedEvent) && !loading && !error && !passwordRequired && !(event?.passwordEnabled && !passwordValidated);
  const isBaptismEvent = Boolean(
    themedEvent?.includesBaptism || themedEvent?.type === 1
  );
  usePageMeta({
    title: showsEvent && themedEvent ? publicEventPageTitle(themedEvent) : t("events.publicMeta.neutralTitle"),
    description: (showsEvent && themedEvent?.description?.trim()) || t("events.publicMeta.descriptionFallback"),
    locale: effectiveLocale === "en" ? "en" : "ro",
    path: `/@${actualSlug ?? ""}`,
    noindex: !showsEvent || Boolean(viewingDraft) || themedEvent?.searchable === false || isBaptismEvent,
    image: showsEvent && themedEvent?.cover ? themedEvent.cover : undefined,
  });

  const handleShare = async () => {
    if (!themedEvent) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: themedEvent.name,
          text: themedEvent.description || `Join us for ${themedEvent.name}`,
          url: window.location.href,
        });
      } catch (_err) {
        // User cancelled or error occurred
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      toast.success(t('common.copied'));
    }
  };

  // Show password gate if password protection is enabled and not yet validated
  // This handles both cases:
  // 1. Event data returned with passwordEnabled flag (from successful load with session)
  // 2. 401 password_required error (first access without session)
  // The owner's server-confirmed preview (JUB-151) is not asked for the guest password.
  if ((event?.passwordEnabled || passwordRequired) && !passwordValidated && !event?.previewMode && actualSlug) {
    return (
      <PasswordGate
        eventSlug={actualSlug}
        onPasswordValidated={(newAccessToken?: string) => {
          setPasswordValidated(true);
          setPasswordRequired(false); // Clear the password required flag
          if (newAccessToken) {
            setAccessToken(newAccessToken);
          }
          // Reload event data after password validation with the new token
          loadEvent(newAccessToken);
        }}
      />
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-pink-50/30 via-background to-background dark:from-pink-950/10 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto mb-4"></div>
          <p className="text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // JUB-184: a legacy /{locale}/{slug} link to a slug that does not exist is a
  // plain not-found page; the short /@{slug} address keeps the event card.
  if (notFound && !location.pathname.startsWith('/@')) {
    return <NotFound />;
  }

  if (notFound || error || !themedEvent) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-pink-50/30 via-background to-background dark:from-pink-950/10 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="py-12 text-center" data-testid="event-not-found">
            <p className="text-destructive mb-4">{notFound ? t('events.notFound') : error || t('events.notFound')}</p>
            <Button onClick={() => window.location.href = '/'}>
              {t('common.goHome')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!themedEvent || !themedEvent.template || !themedEvent.template.config) {
    console.error('Missing template data:', { themedEvent, displayEvent, event });
    return (
      <div className="min-h-screen bg-gradient-to-b from-pink-50/30 via-background to-background dark:from-pink-950/10 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="py-12 text-center">
            <p className="text-destructive mb-4">
              {t('events.templateDataMissing')}
            </p>
            <Button onClick={() => window.location.href = '/'}>
              {t('common.goHome')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-[800px] bg-[#faf9f7] relative overflow-hidden">
      {viewingDraft && <PreviewDraftBanner />}
      {resolvedTemplate.kind === "package" ? (
        <TemplatePageHost
          pkg={resolvedTemplate.pkg}
          // The raw event, not `themedEvent`: the engine applies the owner's
          // colour scheme itself, against the package's own tokens.
          event={displayEventWithComputedFlags!}
          locale={effectiveLocale}
          config={packageSurfaceConfig}
          previewToken={new URLSearchParams(location.search).get(PREVIEW_PARAM)}
        />
      ) : (
        renderEventTemplate({
          event: themedEvent,
          config: themedEvent.template.config,
          locale: effectiveLocale,
          t: templateT,
          onShare: handleShare,
        })
      )}
    </div>
  );
}

const toTemplateTranslator =
  (translate: (key: string, params?: Record<string, string | number>) => string) =>
  (key: string, params?: Record<string, string | number | undefined>): string => {
    if (!params) return translate(key);
    const defined: Record<string, string | number> = {};
    for (const [name, value] of Object.entries(params)) {
      if (value !== undefined) defined[name] = value;
    }
    return translate(key, defined);
  };

// `overrides` is optional on the payload (a draft token may only lift the live gate
// for an unpublished event, JUB-125), but this helper is only reached once the caller
// has checked it — so it takes the non-optional shape.
const applyDraftOverrides = (
  event: PublicEvent,
  overrides: LandingDraftOverrides,
): PublicEvent => {
  const patched: PublicEvent = { ...event };
  if (overrides.theme !== undefined) {
    patched.theme = overrides.theme;
    patched.template =
      patched.template && patched.template.key === overrides.theme
        ? patched.template
        : getFallbackTemplate(overrides.theme);
  }
  if (overrides.fontFamily !== undefined) patched.fontFamily = overrides.fontFamily;
  if (overrides.fontScale !== undefined) patched.fontScale = overrides.fontScale;
  if (overrides.story !== undefined) patched.story = overrides.story ?? undefined;
  if (overrides.gallery) patched.gallery = overrides.gallery;
  if (overrides.passwordEnabled !== undefined) patched.passwordEnabled = overrides.passwordEnabled;
  if (overrides.searchable !== undefined) patched.searchable = overrides.searchable;
  if (overrides.registryPublic !== undefined) patched.registryPublic = overrides.registryPublic;
  if (overrides.family) patched.family = overrides.family;
  if (overrides.ceremonies) {
    patched.ceremonies = {
      ...event.ceremonies,
      ...overrides.ceremonies,
    };
  }
  if (overrides.slug) {
    patched.url = overrides.slug;
  }
  if (overrides.colorScheme !== undefined) {
    patched.colorScheme = overrides.colorScheme ?? undefined;
  }
  return patched;
};
